import { Icon } from "Components/atoms/Icon";
import React, { useRef, useState } from "react";
import { useClickOutside } from "shared/hooks/useClickOutside";
import {
  CHANNEL_LABELS,
  channelsFromFlags,
  formatBRL,
  formatDateFullLong,
  formatDateRangeShort,
  getAirportLabel,
} from "shared/utils/alerts/alertFormat";
import { getMatchedOfferUrl } from "shared/utils/alerts/offerLink";
import {
  Badge,
  Badges,
  BodyWrapper,
  Card,
  CardHeader,
  Footer,
  FooterLabel,
  FullRow,
  Menu,
  MenuButton,
  MenuItemButton,
  MenuItemLink,
  MenuWrapper,
  Price,
  Row,
  RowLabel,
  RowSubLine,
  RowValue,
  OfferCity,
  OfferDate,
  OfferInfo,
  OfferPrice,
  OfferStrip,
  OfferTitle,
  OfferValue,
  StatusLabel,
} from "./styles";
import { AlertCardProps } from "./types";

/**
 * Cartão de um alerta de preço, com status, rotas, datas, preço máximo e menu
 * de ações (editar, ativar/desativar e apagar).
 */
const AlertCard: React.FC<AlertCardProps> = ({
  alert,
  busy,
  onToggle,
  onDelete,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const refs = useRef([menuRef]).current;
  const closeMenu = React.useCallback(() => setIsMenuOpen(false), []);
  useClickOutside(refs, closeMenu);

  const isOpenDates = alert.matchType === "onlyPrice";

  const renderAirportBlock = (codes: string[]) => {
    const code = codes[0] || "";
    const label = getAirportLabel(code);
    const airports =
      label.airports && label.airports.length > 0
        ? label.airports.join(", ")
        : label.airportName
          ? `${label.airportName} (${code})`
          : "Todos os aeroportos";
    return { city: label.city, subline: airports };
  };

  const offer = alert.offerMatched;
  const offerUrl = offer ? getMatchedOfferUrl(offer) : null;

  return (
    <Card $inactive={alert.isStopped}>
      {offer && (
        <OfferStrip
          as={offerUrl ? "a" : "div"}
          {...(offerUrl
            ? { href: offerUrl, target: "_blank", rel: "noreferrer" }
            : {})}
        >
          <OfferInfo>
            <OfferTitle>Passagem encontrada</OfferTitle>
            <OfferDate>{formatDateFullLong(offer.departureDate)}</OfferDate>
          </OfferInfo>
          <OfferPrice>
            <OfferCity>{getAirportLabel(offer.originAirport).city}</OfferCity>
            <OfferValue>
              {formatBRL(offer.lowestAdultPrice, { compact: true })}
            </OfferValue>
          </OfferPrice>
        </OfferStrip>
      )}
      <CardHeader $flat={Boolean(offer)}>
        <StatusLabel>
          {alert.isStopped ? "Alerta inativo" : "Alerta ativo"}
        </StatusLabel>
        <MenuWrapper ref={menuRef}>
          <MenuButton
            type="button"
            aria-label="Opções do alerta"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            disabled={busy}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <Icon library="md" name="MdMoreVert" size="extraLarge" />
          </MenuButton>
          {isMenuOpen && (
            <Menu role="menu">
              <MenuItemLink
                role="menuitem"
                to={`/alertas/${alert.id}/editar`}
                onClick={closeMenu}
              >
                Editar
              </MenuItemLink>
              <MenuItemButton
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu();
                  onToggle(alert);
                }}
              >
                {alert.isStopped ? "Ativar alerta" : "Desativar alerta"}
              </MenuItemButton>
              <MenuItemButton
                type="button"
                role="menuitem"
                $danger
                onClick={() => {
                  closeMenu();
                  onDelete(alert);
                }}
              >
                Apagar
              </MenuItemButton>
            </Menu>
          )}
        </MenuWrapper>
      </CardHeader>

      <BodyWrapper>
        {/* Row 1, Col 1: Origin */}
        {(() => {
          const origin = renderAirportBlock(alert.originAirports);
          return (
            <Row>
              <RowLabel>Partindo de</RowLabel>
              <RowValue>{origin.city}</RowValue>
              <RowSubLine>{origin.subline}</RowSubLine>
            </Row>
          );
        })()}

        {/* Row 1, Col 2: Dates or Open Dates */}
        <Row>
          <RowLabel>{isOpenDates ? "Datas de ida" : "Datas de ida"}</RowLabel>
          <Badges>
            {isOpenDates ? (
              <Badge $variant="open">Datas abertas</Badge>
            ) : alert.departureDate.length > 0 ? (
              <Badge $variant="outbound">
                {formatDateRangeShort(alert.departureDate)}
              </Badge>
            ) : null}
          </Badges>
        </Row>

        {/* Row 2, Col 1: Destination */}
        {(() => {
          const destination = renderAirportBlock(alert.destinationAirports);
          return (
            <Row>
              <RowLabel>Destino</RowLabel>
              <RowValue>{destination.city}</RowValue>
              <RowSubLine>{destination.subline}</RowSubLine>
            </Row>
          );
        })()}

        {/* Row 2, Col 2: Return Dates (empty for open-dates alerts) */}
        {!isOpenDates && alert.returnDate.length > 0 ? (
          <Row>
            <RowLabel>
              {alert.returnDate.length === 1
                ? "Data de volta"
                : "Datas de volta"}
            </RowLabel>
            <Badges>
              <Badge $variant="return">
                {formatDateRangeShort(alert.returnDate)}
              </Badge>
            </Badges>
          </Row>
        ) : null}

        {/* Canais de notificação */}
        <FullRow>
          <RowLabel>Notificar por</RowLabel>
          <Badges>
            {channelsFromFlags(alert).map((channel) => (
              <Badge key={channel} $variant="channel">
                {CHANNEL_LABELS[channel]}
              </Badge>
            ))}
          </Badges>
        </FullRow>
      </BodyWrapper>

      <Footer>
        <FooterLabel>Por menos de</FooterLabel>
        <Price>{formatBRL(alert.maxPrice, { compact: true })}</Price>
      </Footer>
    </Card>
  );
};

export default AlertCard;
